import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../api';
import { useOutletContext, useNavigate } from 'react-router-dom';
import MinisterAIAssistant from '../components/MinisterAIAssistant';

const MinisterDashboard = () => {
    const { currentDept } = useOutletContext();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        pendingAgendas: 0,
        activeBills: 0,
        openQuestions: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (currentDept) fetchStats();
    }, [currentDept]);

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/minister/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(res.data);
            setLoading(false);
        } catch (err) {
            console.error('Failed to fetch dashboard stats');
            setLoading(false);
        }
    };

    const Widget = ({ title, count, icon, color, onClick, label }) => (
        <div
            onClick={onClick}
            style={{
                backgroundColor: '#FFFFFF',
                padding: '30px',
                borderRadius: '24px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
                flex: 1,
                minWidth: '260px',
                border: '1px solid #F0E8DB',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
            }}
            onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(44, 26, 16, 0.08)';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.03)';
            }}
        >
            <div style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '100px',
                height: '100px',
                backgroundColor: `${color}08`,
                borderRadius: '50%'
            }}></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', position: 'relative' }}>
                <span style={{ color: '#999', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px' }}>{title}</span>
                <span style={{ color: color, display: 'flex' }}>{icon}</span>
            </div>
            <div style={{
                fontSize: '32px',
                fontWeight: '800',
                color: '#C0722C',
                marginBottom: '8px',
                position: 'relative',
                fontFamily: "'Outfit', sans-serif",
                letterSpacing: '-0.5px'
            }}>
                {count}
            </div>
            <div style={{ fontSize: '14px', color: '#777', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '6px', height: '6px', backgroundColor: color, borderRadius: '50%' }}></span>
                {label}
            </div>
        </div>
    );

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: '20px' }}>
            <div style={{ width: '40px', height: '40px', border: '3px solid #F0E8DB', borderTopColor: '#C0722C', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            <div style={{ color: '#888', fontWeight: '600', letterSpacing: '1px' }}>SYNCHRONIZING SECURE DATA...</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    return (
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ marginBottom: '50px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                    <h1 style={{ fontSize: '36px', fontWeight: '900', color: '#2C1A10', margin: '0 0 12px 0', letterSpacing: '-0.5px' }}>Executive Overview</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '30px', height: '2px', backgroundColor: '#C0722C' }}></div>
                        <p style={{ fontSize: '17px', color: '#666', margin: 0, fontWeight: '500' }}>
                            Strategic insights for the <span style={{ color: '#C0722C', fontWeight: '800' }}>Department of {currentDept?.name || '...'}</span>
                        </p>
                    </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '12px', fontWeight: '800', color: '#888', textTransform: 'uppercase', marginBottom: '4px' }}>Last Updated</div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#2C1A10' }}>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} Today</div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '35px', marginBottom: '50px' }}>
                <Widget
                    title="Agenda Items"
                    count={stats.pendingAgendas}
                    icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>}
                    color="#C0722C"
                    label="Pending Review"
                    onClick={() => navigate('/minister/agendas')}
                />
                <Widget
                    title="Active Legislation"
                    count={stats.activeBills}
                    icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><path d="M16 13H8"></path><path d="M16 17H8"></path><path d="M10 9H8"></path></svg>}
                    color="#2E7D32"
                    label="In Legislative Process"
                    onClick={() => navigate('/minister/bills')}
                />
                <Widget
                    title="Legislative Inquiries"
                    count={stats.openQuestions}
                    icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>}
                    color="#1565C0"
                    label="Awaiting Ministerial Response"
                    onClick={() => navigate('/minister/questions')}
                />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1fr', gap: '35px' }}>
                <div style={{
                    padding: '40px',
                    backgroundColor: '#fff',
                    borderRadius: '30px',
                    border: '1px solid #F0E8DB',
                    boxShadow: '0 10px 30px rgba(44, 26, 16, 0.03)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                        <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#2C1A10' }}>Priority Actions</h3>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#C0722C', cursor: 'pointer' }}>VIEW ALL ACTIVITY →</div>
                    </div>

                    <div style={{ display: 'flex', gap: '20px' }}>
                        <button
                            onClick={() => navigate('/minister/bills')}
                            style={{
                                flex: 1, padding: '24px', backgroundColor: '#FDF8F3', border: '1.5px solid #F5E6D3',
                                borderRadius: '20px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.3s'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'scale(1.02)';
                                e.currentTarget.style.borderColor = '#C0722C';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.borderColor = '#F5E6D3';
                            }}
                        >
                            <div style={{
                                width: '40px', height: '40px', backgroundColor: '#C0722C', borderRadius: '12px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', marginBottom: '15px'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                            </div>
                            <div style={{ fontWeight: '850', color: '#2C1A10', marginBottom: '8px', fontSize: '18px' }}>Draft Legislation</div>
                            <div style={{ fontSize: '13px', color: '#6B6B6B', lineHeight: '1.6', fontWeight: '500' }}>Initiate a new departmental bill for the upcoming assembly session.</div>
                        </button>

                        <button
                            onClick={() => navigate('/minister/agendas')}
                            style={{
                                flex: 1, padding: '24px', backgroundColor: '#FDF8F3', border: '1.5px solid #F5E6D3',
                                borderRadius: '20px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.3s'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'scale(1.02)';
                                e.currentTarget.style.borderColor = '#C0722C';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.borderColor = '#F5E6D3';
                            }}
                        >
                            <div style={{
                                width: '40px', height: '40px', backgroundColor: '#2C1A10', borderRadius: '12px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EAD7BB', marginBottom: '15px'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                            </div>
                            <div style={{ fontWeight: '850', color: '#2C1A10', marginBottom: '8px', fontSize: '18px' }}>New Agenda Item</div>
                            <div style={{ fontSize: '13px', color: '#6B6B6B', lineHeight: '1.6', fontWeight: '500' }}>Propose critical topics for immediate discussion in the cabinet.</div>
                        </button>
                    </div>
                </div>

                <div style={{
                    padding: '40px',
                    backgroundColor: '#2C1A10',
                    borderRadius: '30px',
                    color: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(44, 26, 16, 0.15)'
                }}>
                    {/* Background Pattern */}
                    <div style={{ position: 'absolute', top: 0, right: 0, width: '100%', height: '100%', opacity: 0.05, pointerEvents: 'none' }}>
                        <svg width="100%" height="100%"><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="white" strokeWidth="0.5" /></pattern><rect width="100%" height="100%" fill="url(#grid)" /></svg>
                    </div>

                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '25px' }}>
                            <div style={{ padding: '8px', backgroundColor: '#C0722C', borderRadius: '8px' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" color="white"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                            </div>
                            <h3 style={{ margin: 0, fontSize: '20px', color: '#EAD7BB', fontWeight: '800' }}>Security Protocol</h3>
                        </div>
                        <div style={{ fontSize: '15px', lineHeight: '1.7', opacity: 0.9, marginBottom: '25px', fontWeight: '500' }}>
                            All departmental communications and legislative drafts are protected by end-to-end encryption.
                        </div>
                    </div>

                    <div style={{
                        position: 'relative',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px',
                        padding: '15px 20px',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderRadius: '15px'
                    }}>
                        <div style={{ position: 'relative' }}>
                            <div style={{ width: '12px', height: '12px', backgroundColor: '#4CAF50', borderRadius: '50%' }}></div>
                            <div style={{ position: 'absolute', top: 0, left: 0, width: '12px', height: '12px', backgroundColor: '#4CAF50', borderRadius: '50%', animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite' }}></div>
                        </div>
                        <span style={{ fontWeight: '800', fontSize: '13px', letterSpacing: '2px' }}>SESSION ACTIVE</span>
                        <style>{`@keyframes ping { 75%, 100% { transform: scale(2.5); opacity: 0; } }`}</style>
                    </div>
                </div>
            </div>
            <MinisterAIAssistant />
        </div>
    );
};

export default MinisterDashboard;
